import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Briefcase, User, ArrowUpRight, Sparkles } from 'lucide-react';
import Reveal from '../Reveal';
import { SectionHeading } from '../shared';

const SESSION_KEY = 'dts_advisor_session';
const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

const trackCardClick = (profile) => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/advisor/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getSession(),
        event: 'solution_click',
        profile,
        target: `help-${profile}`,
      }),
      keepalive: true,
    });
  } catch (e) {}
};

// Servizi condivisi tra Azienda e Professionista — mantenuti identici per
// richiesta esplicita dell'utente. L'ordine riflette il percorso di valore
// consigliato (formazione obbligatoria → presenza digitale → acquisizione
// clienti → pagamenti → AI).
const SHARED_BUSINESS_BULLETS = [
  { label: 'Formazione obbligatoria e certificata', href: '/formazione/corsi-certificati' },
  { label: 'Abilitazione ACDT in 20h',              href: '/formazione/consulente-digitale-sostenibilita' },
  { label: 'Siti web e e-commerce',                 href: '/servizi/siti-web' },
  { label: 'Campagne ADS e funnel',                 href: '/servizi/campagne-ads' },
  { label: 'Sistemi pubblicitari',                  href: '/servizi/social-video' },
  { label: 'Creazione video impattanti',            href: '/servizi/social-video' },
  { label: 'Gestione social',                       href: '/servizi/social-video' },
  { label: 'Chargebull',                            href: '/energy-power' },
  { label: 'Totem',                                 href: '/servizi/chargebull-free' },
  { label: 'AI Act e automazioni',                  href: '/consulenza-ai-act' },
  { label: 'POS SpazioPOS da 0,55%',                href: '/servizi/pos-spaziopos' },
  { label: 'Comparatore Gas e Luce',                href: '/servizi/compara-semplice' },
  { label: 'Tessera + Registro A.C.D.T.',           href: '/acdt' },
];

const PROFILES = [
  {
    id: 'azienda',
    icon: Building2,
    kicker: 'per aziende',
    title: 'AZIENDA',
    desc: 'Trasforma la tua impresa con AI, software su misura, siti web, campagne che portano risultati concreti — e tutta la formazione obbligatoria dei tuoi collaboratori.',
    bullets: SHARED_BUSINESS_BULLETS,
    cta: 'Scopri le soluzioni',
    href: '/servizi?profilo=azienda',
    accent: 'from-lime/20 to-lime/5',
    border: 'border-lime/40',
  },
  {
    id: 'professionista',
    icon: Briefcase,
    kicker: 'per professionisti',
    title: 'PROFESSIONISTA',
    desc: 'Diventa Consulente Digitale ACDT, acquisisci competenze AI e nuove opportunità professionali — con tutti i servizi digitali DTS a disposizione.',
    bullets: SHARED_BUSINESS_BULLETS,
    cta: 'Diventa certificato',
    href: '/formazione/consulente-digitale-sostenibilita',
    accent: 'from-cyan-400/20 to-cyan-400/5',
    border: 'border-cyan-400/40',
  },
  {
    id: 'privato',
    icon: User,
    kicker: 'per privati',
    title: 'PRIVATO',
    desc: 'Attiva i servizi digitali essenziali, acquista corsi certificati online e risparmia su luce e gas con la consulenza DTS.',
    bullets: [
      { label: 'Guadagna un extra o full-time col digitale', href: '/guadagna-con-il-digitale' },
      { label: 'Abilitazione ACDT',                     href: '/formazione/consulente-digitale-sostenibilita' },
      { label: 'Corsi certificati online',              href: '/formazione/corsi-certificati' },
      { label: 'Comparatore Gas e Luce',                href: '/servizi/compara-semplice' },
      { label: 'Gestione social',                       href: '/servizi/social-video' },
      { label: 'Creazione video impattanti',            href: '/servizi/social-video' },
      { label: 'Smart Card & Token USB',                href: '/servizi/punto-semplice' },
      { label: 'SPID - identità digitale',              href: '/servizi/punto-semplice' },
      { label: 'Firma Digitale',                        href: '/servizi/punto-semplice' },
      { label: 'PEC - casella certificata',             href: '/servizi/punto-semplice' },
    ],
    cta: 'Scopri i servizi digitali',
    href: '/servizi/punto-semplice',
    accent: 'from-fuchsia-400/20 to-fuchsia-400/5',
    border: 'border-fuchsia-400/40',
  },
];

export default function HelpByProfile() {
  const navigate = useNavigate();
  return (
    <section className="py-20 md:py-28 bg-background" data-testid="home-help-by-profile">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <SectionHeading
            label="come possiamo aiutarti"
            title="Dicci chi sei,"
            highlight="trova la tua soluzione"
          />
          <p className="text-white/55 text-lg mt-6 max-w-2xl">
            Ogni esigenza ha il suo percorso. Scegli il profilo che ti rappresenta e scopri cosa possiamo fare insieme.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {PROFILES.map((p, i) => (
            <Reveal key={p.id} delay={i * 100}>
              <Link
                to={p.href}
                onClick={() => trackCardClick(p.id)}
                data-testid={`help-card-${p.id}`}
                className={`group relative block rounded-3xl border-2 ${p.border} p-8 h-full overflow-hidden transition-all hover:scale-[1.02] hover:shadow-2xl`}
                style={{ background: `linear-gradient(160deg, ${p.id === 'azienda' ? 'rgba(198,242,78,0.08)' : p.id === 'professionista' ? 'rgba(103,232,249,0.08)' : 'rgba(240,171,252,0.08)'}, rgba(7,7,9,0.4))` }}
              >
                <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
                <div className="relative">
                  <span className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-gradient-to-br ${p.accent}`}>
                    <p.icon size={26} className="text-white" />
                  </span>
                  <p className="mono-label mb-2">_{p.kicker}</p>
                  <h3 className="font-display font-black text-3xl md:text-4xl text-white mb-4 tracking-tight">
                    {p.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-5">{p.desc}</p>
                  <ul className="space-y-2 mb-7">
                    {p.bullets.map((b) => {
                      const label = typeof b === 'string' ? b : b.label;
                      const href = typeof b === 'string' ? null : b.href;
                      if (href && href !== p.href) {
                        return (
                          <li key={label} className="flex items-start gap-2.5 text-sm">
                            <Sparkles size={13} className="text-lime mt-0.5 shrink-0" />
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); trackCardClick(p.id); navigate(href); }}
                              data-testid={`help-${p.id}-bullet-link`}
                              className="text-white/75 hover:text-lime underline-offset-4 hover:underline text-left transition-colors"
                            >
                              {label}
                            </button>
                          </li>
                        );
                      }
                      return (
                        <li key={label} className="flex items-start gap-2.5 text-white/60 text-sm">
                          <Sparkles size={13} className="text-lime mt-0.5 shrink-0" />
                          <span>{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <span className="inline-flex items-center gap-2 text-lime font-semibold text-sm group-hover:gap-3 transition-all">
                    {p.cta} <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-12 text-center">
            <p className="text-white/55 text-sm mb-4">Non sei sicuro? Lascia che l'IA ti guidi.</p>
            <button
              type="button"
              onClick={() => {
                document.querySelector('[data-testid="advisor-launcher"]')?.click();
                try {
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/advisor/track`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: getSession(), event: 'help_open_advisor' }),
                    keepalive: true,
                  });
                } catch (e) {}
              }}
              data-testid="help-open-advisor"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-lime text-lime font-semibold hover:bg-lime hover:text-background transition-all hover:scale-105"
            >
              <Sparkles size={16} /> Parla con il Digital Advisor
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
